import React, { useCallback, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import BrowserFrame from "./BrowserFrame";
import SiteShot from "./SiteShot";
import { EASE } from "./Reveal";

/**
 * Clickable portfolio showcase. Deliberately does NOT hijack the scroll — the page
 * has exactly one pinned scroll moment (the build sequence) and this section stays
 * calm so the journey through the page reads clearly.
 *
 * Two ways to move: the domain tabs above the frame, and prev/next below it.
 */
export function WorkShowcase({ sites, heading }) {
  const [index, setIndex] = useState(0);
  const site = sites[index];

  const step = useCallback(
    (delta) => setIndex((i) => (i + delta + sites.length) % sites.length),
    [sites.length]
  );

  return (
    // On desktop the section is exactly one viewport tall and the frame takes
    // whatever height is left, so heading + tabs + caption + screenshot always
    // land on a single screen. Mobile keeps normal flow with a 16:10 frame.
    <div className="px-5 py-16 sm:px-8 lg:flex lg:min-h-[100svh] lg:flex-col lg:pb-12 lg:pt-24">
      <div className="mx-auto w-full max-w-6xl lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
        <div className="lg:shrink-0">{heading}</div>

        {/* Domain index. This used to be a horizontal scroller on mobile, which put
            the row off the right edge of the phone with nothing to say it scrolled —
            it just read as broken. It now wraps into a two-column grid so every
            domain is on screen, and only becomes a single pill row once there is
            width for all five. */}
        <div className="mt-10 lg:mt-7 lg:shrink-0">
          <div
            className="grid grid-cols-2 gap-1 rounded-2xl border border-[#1A2A44] bg-[#080F1C] p-1 sm:grid-cols-5 sm:rounded-full"
            role="tablist"
            aria-label="Client websites"
          >
            {sites.map((s, i) => (
              <button
                key={s.domain}
                type="button"
                role="tab"
                aria-selected={index === i}
                onClick={() => setIndex(i)}
                // An odd count leaves a gap on the two-column grid; the last tab
                // takes the full row rather than leaving a hole beside it.
                className={`dp-mono dp-focus relative min-w-0 rounded-full px-3 py-2.5 text-center text-[11.5px] tracking-tight transition-colors duration-300 last:odd:col-span-2 sm:px-3.5 sm:text-[12px] sm:last:odd:col-span-1 ${
                  index === i ? "text-white" : "text-[#7E93B2] hover:text-[#B6C6DE]"
                }`}
              >
                {index === i && (
                  <motion.span
                    layoutId="work-tab"
                    className="absolute inset-0 rounded-full bg-[#16233A]"
                    transition={{ duration: 0.4, ease: EASE }}
                  />
                )}
                <span className="relative block truncate">{s.domain}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Title/description sit in a left rail beside a squarer frame, so the
            screenshots stop reading as stretched letterbox strips. */}
        <div className="mt-6 grid gap-6 lg:mt-5 lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(0,19rem)_minmax(0,1fr)] lg:items-start lg:gap-10">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between lg:flex-col lg:items-start lg:gap-7 lg:pt-1">
          <motion.div
            key={site.name}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="min-w-0"
          >
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h3 className="dp-display text-xl font-semibold tracking-[-0.02em] text-[#EDF1F7]">
                {site.name}
              </h3>
              <span className="dp-mono text-[10.5px] uppercase tracking-[0.16em] text-[#5C749B]">
                {site.sector}
              </span>
            </div>
            <p className="mt-1.5 text-[14.5px] leading-relaxed text-[#8FA3C0]">
              {site.description}
            </p>
          </motion.div>

          <div className="flex shrink-0 items-center gap-3">
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => step(-1)}
                aria-label="Previous site"
                className="dp-focus inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#25395A] text-[#B6C6DE] transition-colors duration-300 hover:border-[#3D5680] hover:bg-[#0E1929] hover:text-white"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </button>
              <span className="dp-mono w-14 text-center text-[11px] tracking-[0.14em] text-[#5C749B]">
                {String(index + 1).padStart(2, "0")}/{String(sites.length).padStart(2, "0")}
              </span>
              <button
                type="button"
                onClick={() => step(1)}
                aria-label="Next site"
                className="dp-focus inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#25395A] text-[#B6C6DE] transition-colors duration-300 hover:border-[#3D5680] hover:bg-[#0E1929] hover:text-white"
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            <a
              href={site.url}
              target="_blank"
              rel="noopener noreferrer"
              className="dp-focus group inline-flex items-center gap-2 rounded-full border border-[#25395A] px-5 py-2.5 text-[13.5px] font-medium text-[#EDF1F7] transition-colors duration-300 hover:border-[#FF6A3D]/60 hover:bg-[#0E1929]"
            >
              Open site
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </div>

        {/* On desktop the height drives the width, so the 4:3 ratio is preserved
            while the frame still sizes itself to the space available. */}
        <div className="mt-5 flex aspect-[4/3] justify-center lg:mt-0 lg:aspect-auto lg:h-full lg:min-h-0 lg:items-center">
          <div
            className="flex h-full w-full flex-col lg:w-auto lg:max-w-full"
            style={{ aspectRatio: "4 / 3" }}
          >
          <BrowserFrame url={`https://${site.domain}`} fill className="min-h-0 flex-1">
            <a
              href={site.url}
              target="_blank"
              rel="noopener noreferrer"
              // Fills the frame's (relative) viewport box directly. A percentage
              // height here resolves to 0 inside the min-h-0 flex chain.
              className="dp-focus group absolute inset-0 block overflow-hidden"
            >
              {/* Only the active screenshot is mounted, so at most two decode at once. */}
              <SiteShot
                key={site.image}
                image={site.image}
                alt={`${site.name} website, built by DataPulse Web`}
                className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-[1.4s] ease-out group-hover:scale-[1.02]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, ease: EASE }}
              />
            </a>
          </BrowserFrame>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

export default WorkShowcase;
